import BaseActionFilter from 'yoda-core-components/lib/helpers/Analytics/BaseActionFilter';
import { selectDeviceName } from '../selectors/ContextSelector';
import { initProductOptions } from '../selectors/ProductOptionsSelector';
import productPricingSelector, {
    selectSaleandClearence,
    productPricingSelectorForAllLot,
} from '../selectors/ProductPricingSelector';
import selectProductType from '../selectors/productDetails/selectProductType';
import { selectProductDetailsWrapper } from '../selectors/PdpNameSpaceSelector';
import constants from './PDPAnalytics.config';
import PDPAnalytics, { decimalRounding } from './PDPAnalytics';
import snapshotDigitalData from '../actions/DigitalDataAction';
import { keyBy } from '../helpers/Utils/lodashReplace';

const defaults = {
    breadcrumbsData: [],
    contextAttributes: {},
};

class PDPAnalyticsActionFilter extends BaseActionFilter {
    getPdpPageDL(store, payload, bundleProductPricingSelector, bundleLotsPricingSelector) {
        const { sourceMiniPDP = false } = payload;
        // take a snapshot of underlying digitaldata
        if (sourceMiniPDP) {
            store.dispatch(snapshotDigitalData());
        }

        const stateObj = store.getState();

        let itemsInSaveForLater = 0;
        /* istanbul ignore else */
        if (stateObj.list && stateObj.list.savedItems) {
            itemsInSaveForLater = stateObj.list.savedItems.length;
        }
        itemsInSaveForLater += '';

        const deviceType = selectDeviceName(stateObj);
        const productDetails = selectProductDetailsWrapper(sourceMiniPDP)(stateObj);

        const breadcrumbs = productDetails?.breadCrumbInfo?.breadcrumbs || defaults.breadcrumbsData;

        /* istanbul ignore next */
        let category1 = '';
        let category2 = '';
        let categoryparent = '';
        if (breadcrumbs && breadcrumbs.length > 0) {
            category1 = breadcrumbs[2] ? breadcrumbs[2]?.breadCrumbLabel || '' : '';
            category2 = breadcrumbs[3] ? breadcrumbs[2]?.breadCrumbLabel || '' : '';
            categoryparent = breadcrumbs[1] ? breadcrumbs[1]?.breadCrumbLabel || '' : '';
        }

        let couponEligible = '';
        let couponType = '';
        let couponDiscount = '';
        let couponAmount = '';
        let couponCode = '';
        let couponApplyMethod = '';
        let { contextAttributes } = defaults;

        const lots = stateObj?.productDetails?.lots || [];

        const getDefaultLot = lots.filter(({ isDefault }) => isDefault);
        // check if default lot is there otherwise pick the first lot.
        if (getDefaultLot.length) {
            contextAttributes = getDefaultLot[0]?.contextAttributes || defaults.contextAttributes;
        } else {
            contextAttributes = lots[0]?.contextAttributes || defaults.contextAttributes;
        }

        // transform context attributes to key value pair for easy access.
        contextAttributes = keyBy(contextAttributes, 'name');

        /* istanbul ignore next */
        if (stateObj.context.featureFlags && payload.inventory && payload.inventory.length) {
            const defaultLotID = getDefaultLot[0]?.id || '';

            if (defaultLotID) {
                const initDefaultLotPrice = payload?.inventory[0]?.lotPrice?.data || [];
                const defaultLotPrice = initDefaultLotPrice?.filter(
                    ({ id }) => id === defaultLotID
                );
                const couponInfo = defaultLotPrice[0]?.couponInfo || [];

                if (couponInfo && couponInfo.length) {
                    const { adjustments = [], alphaId, amount = {} } = couponInfo[0];
                    if (amount.max && amount.min && amount.max === amount.min) {
                        adjustments.forEach(({ min, type }) => {
                            if (type === 'PERCENTAGEOFF') {
                                couponType = type.toLowerCase();
                                couponDiscount = decimalRounding(min);
                            }
                            if (type === 'DOLLAROFF') {
                                couponAmount = decimalRounding(min);
                            }
                        });
                        couponEligible = 'yes';
                        couponCode = alphaId ? alphaId.toLowerCase() : '';
                        couponApplyMethod = 'pdp load';
                    }
                }
            }
        }
        /* istanbul ignore next */
        const primaryCategory = payload.searchAvailable ? 'search' : categoryparent;
        let { templateType } = payload;
        if (!templateType) {
            templateType = selectProductType(sourceMiniPDP)(stateObj).templateType;
        }
        const pageType = sourceMiniPDP ? constants.MINI_PDP_PAGE_ID : constants.PDP_PAGE_ID;
        const version = `apollo 1 pdp - ${templateType}`;
        const bundleProductPrice =
            payload.isBundle &&
            bundleProductPricingSelector &&
            bundleProductPricingSelector(stateObj);
        /* istanbul ignore next */
        const pdpPageDL = {
            page: {
                category: {
                    subCategory2: category2,
                    subCategory1: category1,
                    primaryCategory,
                    department: primaryCategory,
                },
                pageInfo: {
                    pageType, // home | department | category | gallery | search | pdp | customer service | promotion
                    siteSection: primaryCategory, // home | men | women | for the home | search | gift-card
                    // pageName: '',
                    // destinationURL: document.location.toString(),
                    searchRedirect: this.getURLParameterBykey('redirectTerm') || '',
                    deviceType,
                    // referringURL: document.referrer,
                    releaseVersion: stateObj.context.__RELEASEVERSION__, // eslint-disable-line
                    version,
                    // analyticsPageName: 'd:welcome',
                },
            },
            product: !payload.isBundle
                ? PDPAnalytics.getProductAnalytics(
                      stateObj,
                      productPricingSelector(sourceMiniPDP)(stateObj),
                      productPricingSelectorForAllLot(sourceMiniPDP)(stateObj),
                      payload.collectionView,
                      selectSaleandClearence(sourceMiniPDP)(stateObj),
                      contextAttributes,
                      sourceMiniPDP
                  )
                : PDPAnalytics.getBundleProductAnalytics(
                      stateObj,
                      bundleProductPrice,
                      bundleLotsPricingSelector(stateObj),
                      payload.collectionView,
                      payload.urgencyStatements
                  ),
            user: {
                // this will be common for all Micro pages- But we reading info from store, should be there in each micro site
                profile: {
                    profileInfo: {
                        itemsInSaveForLater,
                    },
                },
            },
            cart: {
                couponRewardsInfo: {
                    couponCode,
                    couponEligible,
                    couponType,
                    couponDiscount,
                    couponAmount,
                    couponApplyMethod,
                },
            },
        };
        return pdpPageDL;
    }

    /* checking inventoryProductOOS */
    getPdpPageDLProductOOS(
        store,
        payload,
        bundleProductPricingSelector,
        bundleLotsPricingSelector
    ) {
        const { sourceMiniPDP = false } = payload;
        const { contextAttributes } = defaults;
        const stateObj = store.getState();
        const bundleProductPrice =
            payload.isBundle &&
            bundleProductPricingSelector &&
            bundleProductPricingSelector(stateObj);
        const pdpPageDL = {
            product: !payload.isBundle
                ? PDPAnalytics.getProductAnalyticsProductOOS(
                      stateObj,
                      productPricingSelector(sourceMiniPDP)(stateObj),
                      productPricingSelectorForAllLot(sourceMiniPDP)(stateObj),
                      payload.collectionView,
                      selectSaleandClearence(sourceMiniPDP)(stateObj),
                      contextAttributes,
                      sourceMiniPDP
                  )
                : PDPAnalytics.getBundleProductAnalyticsProductOOS(
                      stateObj,
                      bundleProductPrice,
                      bundleLotsPricingSelector(stateObj),
                      payload.collectionView,
                      payload.urgencyStatements
                  ),
        };
        return pdpPageDL;
    }

    getCartAddData(store, payload) {
        const stateObj = store.getState();
        /* istanbul ignore next */
        const { sourceMiniPDP = false } = payload;
        const product = PDPAnalytics.getCartAddAnalytics(
            stateObj,
            productPricingSelector(sourceMiniPDP)(stateObj),
            initProductOptions(sourceMiniPDP)(stateObj),
            payload.deliveryMethod,
            payload.cartItems,
            payload.clickPosition,
            payload.maxQuantityMsgDisplay,
            payload.deliveryDayType,
            payload.deliveryDays,
            payload.deliveryDateForAnalytics,
            sourceMiniPDP
        );
        return { product: product.product, cart: product.cart };
    }

    getBundleCartAddData(store, payload, selectedProductsForBundles) {
        const stateObj = store.getState();
        const product = PDPAnalytics.getBundleCartAddAnalytics(
            stateObj,
            selectedProductsForBundles(stateObj),
            payload.deliveryMethod,
            payload.cartItems,
            payload.clickPosition,
            payload.bundleDeliveryDateForAnalytics,
            payload.bundleDeliveryTypeForAnalytics
        );
        return { product: product.product, cart: product.cart };
    }

    getCartUpdateData(store, payload) {
        const stateObj = store.getState();
        /* istanbul ignore next */
        const { sourceMiniPDP = false } = payload;
        const product = PDPAnalytics.getCartUpdateAnalytics(
            stateObj,
            productPricingSelector(sourceMiniPDP)(stateObj),
            initProductOptions(sourceMiniPDP)(stateObj),
            payload
        );
        return { product: product.product, cart: product.cart };
    }

    getPdpToolName(name) {
        const pdpToolName = (name || '').toLowerCase();
        const page = {
            pageInfo: {
                pdpToolName,
            },
        };
        return { page };
    }

    getCheckAvailabilityData(store) {
        const stateObj = store.getState();
        const productInfo = {
            productPPID: stateObj.productDetails.id,
        };
        const product = [
            {
                productInfo,
            },
        ];
        return { product };
    }

    getModalOpenData(modalName) {
        const pageInfo = {
            name: (modalName || '').toLowerCase(),
        };
        const page = {
            pageInfo,
        };
        return { page };
    }

    getProductOOSData(store, payload) {
        const stateObj = store.getState();
        /* istanbul ignore next */
        const { sourceMiniPDP = false } = payload;
        const product = PDPAnalytics.getProductOOSAnalytics(
            stateObj,
            productPricingSelector(sourceMiniPDP)(stateObj),
            initProductOptions(sourceMiniPDP)(stateObj)
        );
        return { product };
    }

    getCarePlanViewData(product) {
        return { product };
    }

    getCarePlanAcceptData(product) {
        return { product };
    }

    /* ErrorMessage should be an array of objects with key : value - [ { productDescription : "productDescription message" }] */
    getFormErrorData(ErrorMessage) {
        const error = ErrorMessage;
        return { error };
    }

    getProductVideoData(videoData) {
        const pageInfo = {
            videoPlayer: videoData,
        };
        const page = {
            pageInfo,
        };
        return { page };
    }

    getStoreSearchData(zipCode) {
        return {
            store: {
                searchText: zipCode,
            },
        };
    }

    getSelectOptionsData() {
        const page = {
            pageInfo: {
                clickPosition: 'select options|pdp|sticky',
            },
        };
        return { page };
    }

    getSelectSkuPDP(payload) {
        return {
            product: [
                {
                    productInfo: {
                        productPPID: payload.productPPID,
                        productName: payload.productName,
                        productLotId: payload.productLotId,
                        productSKU: payload.productSKU,
                        productListPrice: payload.productListPrice,
                        productCurrentSellingPrice: payload.productCurrentSellingPrice,
                        productInStock: payload.productInStock,
                        productDeliveryDays: payload.productDeliveryDays,
                        productDeliveryDayType: payload.productDeliveryDayType,
                        productPromiseDate: payload.productPromiseDate,
                        productPriceWithCoupon: payload.productPriceWithCoupon,
                        productTruckable: payload.productTruckable,
                    },
                },
            ],
        };
    }

    getShopTheLookSuccess(payload = {}) {
        if (payload.page && payload.page.attributes) {
            payload.page.attributes = {
                ...payload.page.attributes,
                shopTheLookModule: 'yes',
            };
        }
        return payload;
    }

    getShopThisLookOnClick(payload) {
        return { moduleName: payload.moduleName, shopTheLookID: payload.shopTheLookID };
    }

    getShopTheLookMiniPDPOnClick(payload) {
        return {
            moduleName: payload.moduleName,
            shopTheLookID: payload.shopTheLookID,
            shopTheLookPPID: payload.shopTheLookPPID,
        };
    }

    getShopTheCollectionSuccess(payload = {}) {
        if (payload.page && payload.page.attributes) {
            payload.page.attributes = {
                ...payload.page.attributes,
                shopTheCollectionModule: 'yes',
            };
        }
        return payload;
    }

    getShopTheSetSuccess(payload = {}) {
        if (payload.page && payload.page.attributes) {
            payload.page.attributes = {
                ...payload.page.attributes,
                shopTheSetModule: 'yes',
            };
        }
        return payload;
    }

    getShopTheSetOnClick(payload) {
        return {
            shopTheSetID: payload.shopTheSetID,
            shopTheSetPPID: payload.shopTheSetPPID,
            moduleName: payload.moduleName,
        };
    }

    getPSCSKUAvailability(payload) {
        return {
            module: {
                name: payload.name,
                linktext: payload.linktext,
                locationpath: payload.analyticsPageName,
                targetpath: '',
                linkposition: 0,
            },
        };
    }
}

export default PDPAnalyticsActionFilter;
